import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiMenu, FiUsers, FiMail, FiPhone, FiCalendar, FiSearch, FiDownload, FiTrash2 } from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import { getAllMembers, removeMember } from '../../Redux/slices/ministrySlice';

const MinistryMembers = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMinistry, setFilterMinistry] = useState('all');
  const [selectedMember, setSelectedMember] = useState(null);
  const dispatch = useDispatch();
  const { members } = useSelector(state => state.ministry);

  useEffect(() => {
    dispatch(getAllMembers());
  }, [dispatch]);

  const ministryOptions = [
    { value: 'worship-team', label: 'Worship Team' },
    { value: 'media-ministry', label: 'Media Ministry' },
    { value: 'creative-arts', label: 'Creative Arts' },
    { value: 'children-ministry', label: "Children's Ministry" },
    { value: 'youth-ministry', label: 'Youth Ministry' },
    { value: 'adult-ministry', label: 'Adult Ministry' },
    { value: 'community-outreach', label: 'Community Outreach' },
    { value: 'missions', label: 'Missions Ministry' },
    { value: 'pastoral-care', label: 'Pastoral Care' },
    { value: 'discipleship', label: 'Discipleship Training' },
    { value: 'small-groups', label: 'Small Groups' },
    { value: 'ushering', label: 'Ushering & Hospitality' },
    { value: 'prayer-team', label: 'Prayer Team' },
  ];

  const handleRemoveMember = async (id) => {
    if (window.confirm('Are you sure you want to remove this member from the ministry?')) {
      await dispatch(removeMember(id));
      dispatch(getAllMembers());
      setSelectedMember(null);
    }
  };

  const filteredMembers = members.filter(member => {
    const matchesSearch = 
      member.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMinistry = filterMinistry === 'all' || member.ministry === filterMinistry;
    return matchesSearch && matchesMinistry;
  });

  // Group members by ministry
  const membersByMinistry = ministryOptions.reduce((acc, ministry) => {
    acc[ministry.value] = members.filter(m => m.ministry === ministry.value).length;
    return acc;
  }, {});

  const totalMembers = members.length;

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} currentPage="ministry-members" />

      <div className="lg:ml-64">
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden">
              <FiMenu size={24} />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Ministry Members</h2>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800">Admin User</p>
                <p className="text-xs text-gray-500">admin@houseofpraise.com</p>
              </div>
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                A
              </div>
            </div>
          </div>
        </header>

        <main className="p-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Total Members</p>
                  <p className="text-3xl font-bold text-gray-800">{totalMembers}</p>
                </div>
                <div className="bg-blue-100 p-4 rounded-lg">
                  <FiUsers className="text-blue-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Worship Team</p>
                  <p className="text-3xl font-bold text-gray-800">{membersByMinistry['worship-team'] || 0}</p>
                </div>
                <div className="bg-purple-100 p-4 rounded-lg">
                  <FiUsers className="text-purple-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Media Ministry</p>
                  <p className="text-3xl font-bold text-gray-800">{membersByMinistry['media-ministry'] || 0}</p>
                </div>
                <div className="bg-green-100 p-4 rounded-lg">
                  <FiUsers className="text-green-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Children's Ministry</p>
                  <p className="text-3xl font-bold text-gray-800">{membersByMinistry['children-ministry'] || 0}</p>
                </div>
                <div className="bg-orange-100 p-4 rounded-lg">
                  <FiUsers className="text-orange-600" size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <select
                value={filterMinistry}
                onChange={(e) => setFilterMinistry(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Ministries</option>
                {ministryOptions.map((ministry) => (
                  <option key={ministry.value} value={ministry.value}>
                    {ministry.label}
                  </option>
                ))}
              </select>

              <button className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition">
                <FiDownload size={18} />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* Members Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Name</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Ministry</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Contact</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Location</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Joined</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-semibold">
                              {member.firstName.charAt(0)}{member.lastName.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="text-gray-800 font-semibold">{member.firstName} {member.lastName}</p>
                            <p className="text-gray-500 text-sm">{member.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                          {member.ministryLabel}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm">
                          <p className="text-gray-800 flex items-center gap-2">
                            <FiMail className="text-gray-400" size={14} />
                            {member.email}
                          </p>
                          <p className="text-gray-600 flex items-center gap-2 mt-1">
                            <FiPhone className="text-gray-400" size={14} />
                            {member.phone}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-600">{member.city}, {member.state}</td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 text-gray-600">
                          <FiCalendar size={14} />
                          <span className="text-sm">{member.joinedDate}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedMember(member)}
                            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleRemoveMember(member._id)}
                            className="text-red-600 hover:text-red-700 font-medium text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Ministry Breakdown */}
          <div className="mt-6 bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Members by Ministry</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ministryOptions.map((ministry) => {
                const count = membersByMinistry[ministry.value] || 0;
                return (
                  <div key={ministry.value} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-sm">{ministry.label}</p>
                        <p className="text-2xl font-bold text-gray-800">{count}</p>
                      </div>
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <FiUsers className="text-blue-600" size={20} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </main>
      </div>

      {/* Member Details Modal */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-800">Member Details</h3>
              <button
                onClick={() => setSelectedMember(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiUsers size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FiUsers className="text-blue-600" />
                  Personal Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="text-gray-800 font-semibold">{selectedMember.firstName} {selectedMember.lastName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-800">{selectedMember.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-gray-800">{selectedMember.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="text-gray-800">{selectedMember.city}, {selectedMember.state}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="text-gray-800">{selectedMember.address}</p>
                  </div>
                </div>
              </div>

              {/* Ministry Information */}
              <div>
                <h4 className="text-lg font-bold text-gray-800 mb-4">Ministry Information</h4>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Ministry</p>
                    <p className="text-gray-800 font-semibold">{selectedMember.ministryLabel}</p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-2">Availability</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedMember.availability.map((time, index) => (
                        <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                          {time}
                        </span>
                      ))}
                    </div>
                  </div>

                  {selectedMember.skills && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Skills & Talents</p>
                      <p className="text-gray-800">{selectedMember.skills}</p>
                    </div>
                  )}

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Joined Date</p>
                    <p className="text-gray-800">{selectedMember.joinedDate}</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleRemoveMember(selectedMember._id)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2"
                >
                  <FiTrash2 size={20} />
                  Remove from Ministry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default MinistryMembers;
